import { useEffect, useState } from "react";
import { AlertTriangle, Eye, LogOut } from "lucide-react";

import { impersonation, type Impersonation } from "@/api/client";

/**
 * THE IMPERSONATION BANNER.
 *
 * The spec says an impersonated session "must show a persistent banner". This is it, and it
 * is the only thing in Tankly allowed to be loud.
 *
 * ── WHY IT LOOKS LIKE THIS ──────────────────────────────────────────────────
 *
 * The failure this component exists to prevent is entirely human: a support engineer walks
 * into a customer's tenant to answer a ticket, gets pulled into a meeting, comes back, and
 * carries on working — believing they are looking at Tankly's own console. Everything they
 * then read is a customer's private data, and they do not know it.
 *
 * The server already makes that SAFE — an impersonated session is refused by every write
 * endpoint, so nothing can be changed. What the server cannot do is make it OBVIOUS. So:
 *
 *   fixed to the top, above everything, and it does not scroll away
 *   the one alarm colour in the product, used nowhere else
 *   it says WHOSE tenant, by name, not "impersonation active"
 *   a live countdown, so "it will expire eventually" becomes "in 26 minutes"
 *   a way out that is always one click away
 *
 * It is not dismissible. A banner you can dismiss is a banner that will be dismissed.
 */
export function ImpersonationBanner({ onExit }: { onExit: () => void }) {
  const [session, setSession] = useState<Impersonation | null>(() => impersonation.get());
  const [remaining, setRemaining] = useState<string>("");

  useEffect(() => {
    if (!session) return;

    const tick = () => {
      const ms = new Date(session.expiresAt).getTime() - Date.now();
      if (ms <= 0) {
        // The session is dead on the server too. Stop pretending it is not.
        impersonation.set(null);
        setSession(null);
        onExit();
        return;
      }
      const minutes = Math.floor(ms / 60_000);
      const seconds = Math.floor((ms % 60_000) / 1000);
      setRemaining(`${minutes}:${String(seconds).padStart(2, "0")}`);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session, onExit]);

  if (!impersonation.isLive(session)) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="impersonation-banner"
      className="impersonation-banner fixed inset-x-0 top-0 z-50 bg-[var(--color-alarm)] text-[var(--color-alarm-ink)] shadow-lg"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 text-sm font-semibold">
        <AlertTriangle aria-hidden className="size-5 shrink-0" />

        <span className="flex items-center gap-2">
          <Eye aria-hidden className="size-4" />
          You are viewing <strong className="font-bold">{session.organizationName}</strong> as{" "}
          <strong className="font-bold">{session.userName}</strong>
        </span>

        <span className="rounded bg-[var(--color-alarm-ink)]/10 px-2 py-0.5 font-normal">
          Read-only — every write is refused
        </span>

        <span className="tabular ml-auto flex items-center gap-2 font-normal">
          <span aria-hidden>expires in</span>
          <strong className="font-semibold" aria-label={`Session expires in ${remaining}`}>
            {remaining}
          </strong>
        </span>

        <button
          type="button"
          onClick={() => {
            impersonation.set(null);
            setSession(null);
            onExit();
          }}
          className="flex items-center gap-1.5 rounded bg-[var(--color-alarm-ink)] px-3 py-1 font-semibold text-[var(--color-alarm)] hover:opacity-90"
        >
          <LogOut aria-hidden className="size-4" />
          Stop impersonating
        </button>
      </div>
    </div>
  );
}
